import { Client, Loan, CreditScore } from '../types';

/**
 * Generates an email template for applying for equipment financing
 */
export const generateEmailTemplate = (
    client: Client,
    loans: Loan[],
    creditScore: CreditScore | null
): string => {
    const companyName = client.company_name;
    const industry = client.industry || 'N/A';
    
    // Calculate total loan amount and equipment count
    const totalLoanAmount = loans.reduce((sum, loan) => sum + loan.total_amount, 0);
    const totalEquipment = loans.reduce((sum, loan) => sum + loan.equipment.length, 0);
    
    // Format currency
    const currency = client.currency || 'USD';
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Get credit score information
    const creditScoreText = creditScore
        ? `${creditScore.overall_score.toFixed(1)}/100 (${creditScore.rating})`
        : 'Pending evaluation';

    // Build equipment list
    const equipmentList = loans.flatMap(loan =>
        loan.equipment.map(eq => {
            const equipmentType = eq.equipment_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return `  • ${equipmentType} - ${eq.make} ${eq.model} (${eq.year})\n    Serial: ${eq.serial_number}\n    Value: ${formatCurrency(eq.appraised_value)}`;
        })
    ).join('\n\n');

    // Build loan summary
    const loanSummary = loans.map(loan => {
        return `  • Loan #${loan.loan_number} - ${formatCurrency(loan.total_amount)} at ${loan.interest_rate}% APR, ${loan.term_months} months (${loan.status})`;
    }).join('\n');

    const emailTemplate = `Subject: Equipment Financing Application - ${companyName}

Dear Lending Officer,

I am writing to submit an application for equipment financing on behalf of ${companyName}, a ${industry} company.

FINANCIAL OVERVIEW
------------------
Company: ${companyName}
Industry: ${industry}
Credit Score: ${creditScoreText}
Total Financing Requested: ${formatCurrency(totalLoanAmount)}
Number of Equipment Items: ${totalEquipment}

EQUIPMENT DETAILS
-----------------
${equipmentList || 'No equipment details available'}

LOAN SUMMARY
------------
${loanSummary || 'No active loans'}

SUPPORTING DOCUMENTATION
------------------------
We have prepared comprehensive financial documentation including:
• Financial statements (Income Statement, Balance Sheet, Cash Flow Statement)
• Credit reports (Equifax, PayNet)
• Equipment appraisals and specifications
• Company identification documents

All supporting documents are available in the attached package for your review.

REQUEST
-------
We respectfully request your consideration for equipment financing based on the following:
• Strong credit profile with a score of ${creditScoreText}
• Well-maintained equipment with documented appraised values
• Established business operations in the ${industry} industry
• Clear repayment capacity demonstrated through our financial statements

We are prepared to provide any additional information or documentation you may require. Please let us know if you need any clarification or have questions about our application.

Thank you for your time and consideration. We look forward to your response.

Best regards,
${companyName}
`;

    return emailTemplate;
};

